'use client'

import Button from '@shared/components/Button/Button'
import { Markdown } from '@shared/components/Markdown/Markdown'
import Text from '@shared/components/Text/Text'
import { Stack } from '@christiankaindl/lyts'
import { theme } from '@shared/styles/theme.css'
import { useTranslations } from 'next-intl'
import { useLayoutEffect, useRef, useState } from 'react'

export function getMarkdownPreview(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export default function Description({
  text,
  size,
  maxLines = 2,
  isMarkdown = false,
  fullWidth = false,
  onReadMore,
}: {
  text: string
  size?: 'small'
  maxLines?: number
  isMarkdown?: boolean
  /** Lifts the global paragraph width cap, except on expanded Markdown. */
  fullWidth?: boolean
  onReadMore?: () => void
}) {
  const t = useTranslations('Regulatory')
  const [expanded, setExpanded] = useState(false)
  // Starts true so the server render keeps the toggle for the usual long text
  // and only short texts shift, once, when the measurement drops it.
  const [isClamped, setIsClamped] = useState(true)
  const clampedTextRef = useRef<HTMLDivElement>(null)
  const previewText = isMarkdown ? getMarkdownPreview(text) : text
  const isCustomReadMore = onReadMore !== undefined

  useLayoutEffect(() => {
    const element = clampedTextRef.current?.firstElementChild
    if (!element || expanded) return
    const measure = () =>
      setIsClamped(element.scrollHeight - element.clientHeight > 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [expanded, previewText, maxLines])

  return (
    <Stack gap={0}>
      {expanded && isMarkdown && !isCustomReadMore ? (
        <Text
          as='div'
          color='secondary'
          size={size}
          className='[&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_*]:text-[1em] [&_li]:leading-[inherit] [&_p]:leading-[inherit]'
        >
          <Markdown markdown={text} />
        </Text>
      ) : (
        <div ref={clampedTextRef}>
          <Text
            maxLines={expanded && !isCustomReadMore ? undefined : maxLines}
            color='secondary'
            size={size}
            style={fullWidth ? { maxWidth: 'none' } : undefined}
          >
            {previewText}
          </Text>
        </div>
      )}
      {/* The Markdown preview drops formatting, so only the expanded view
          shows it. */}
      {(expanded || isClamped || isMarkdown) && (
        <Button
          style={{
            color: theme.colors.text.brand,
            textDecoration: 'underline',
            marginLeft: -6,
          }}
          variant='flat'
          size='tiny'
          color='brand'
          onClick={() => {
            if (onReadMore) {
              onReadMore()
              return
            }
            setExpanded(!expanded)
          }}
        >
          {expanded && !isCustomReadMore ? t('readLess') : t('readMore')}
        </Button>
      )}
    </Stack>
  )
}
